import type { TouchData } from '../types/tracking.js';
import { NEW_SESSION_TIMEOUT_MINUTES } from '../config.js';
import { getTrackingData, writeTrackingData } from '../cookie/tracking-cookie.js';
import { getAllUrlParameters } from './url-params.js';
import { getExternalReferrer, getWebsiteSource } from './referrer.js';
import { extractClickIds } from './click-ids.js';
import { slimUrl, slimReferrer } from './url-slim.js';
import { logger } from '../utils/logger.js';

/**
 * Crée un objet TouchData complet à partir de l'URL et du referrer courants.
 */
export function createTouch(): TouchData {
  const params = getAllUrlParameters();
  const fullReferrer = getExternalReferrer();
  const clickIds = extractClickIds(params);

  return {
    utm_source: params['utm_source'] ?? '',
    utm_medium: params['utm_medium'] ?? '',
    utm_campaign: params['utm_campaign'] ?? '',
    utm_term: params['utm_term'] ?? '',
    utm_content: params['utm_content'] ?? '',
    gclid: clickIds.gclid,
    msclkid: clickIds.msclkid,
    li_fat_id: clickIds.li_fat_id,
    fbclid: clickIds.fbclid,
    referrer: slimReferrer(fullReferrer),
    landing_page: slimUrl(window.location.href),
    website_source: getWebsiteSource(params['utm_source'] ?? '', fullReferrer),
    timestamp: new Date().toISOString(),
  };
}

/**
 * Détecte si `next` représente une nouvelle session par rapport à `current`.
 *
 * Nouvelle session si (OR) :
 * 1. Le referrer a changé
 * 2. Au moins un UTM a changé ET la nouvelle valeur n'est pas vide
 * 3. Plus de NEW_SESSION_TIMEOUT_MINUTES se sont écoulées
 */
export function isNewSession(current: TouchData | undefined, next: TouchData): boolean {
  if (!current) return true;

  const utmFields: (keyof TouchData)[] = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
  ];
  const utmChanged = utmFields.some(
    (f) => current[f] !== next[f] && next[f] !== '',
  );

  const timeDiffMs = new Date(next.timestamp).getTime() - new Date(current.timestamp).getTime();
  const minutesPassed = timeDiffMs / (1000 * 60);

  return (
    current.referrer !== next.referrer ||
    utmChanged ||
    minutesPassed > NEW_SESSION_TIMEOUT_MINUTES
  );
}

/**
 * Applique la logique d'attribution sur le cookie actuel.
 * Appelé une fois par page load par l'orchestrateur.
 *
 * - Première visite → first_touch = last_touch = newTouch
 * - Nouvelle session → last_touch = newTouch (l'ancien n'est plus archivé)
 * - Même session → seul le timestamp du last_touch est rafraîchi
 *
 * Une seule écriture cookie en fin de fonction.
 */
export function updateTouchData(): void {
  const data = getTrackingData();

  // Purge une clé middle_touches héritée d'un ancien cookie (retirée du modèle)
  delete (data as unknown as Record<string, unknown>)['middle_touches'];

  const newTouch = createTouch();

  if (!data.first_touch) {
    // Première visite
    data.first_touch = newTouch;
    data.last_touch = newTouch;
    logger.info('First touch enregistré');
  } else if (isNewSession(data.last_touch, newTouch)) {
    // Nouvelle session
    data.last_touch = newTouch;
    logger.info('Nouvelle session détectée, last_touch mis à jour');
  } else if (data.last_touch) {
    // Même session → juste rafraîchir le timestamp
    data.last_touch = { ...data.last_touch, timestamp: newTouch.timestamp };
  }

  writeTrackingData(data);
}
